import { useState } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { createApiClient } from '../lib/apiClient';
import { setPassword as setPasswordApi } from '../lib/auth/authApi';
import { useAuth } from '../lib/auth/AuthProvider';
import './auth.css';

/**
 * Where the emailed set-password link lands.
 *
 * The one thing this screen must not do is let someone leave believing their
 * password opens their messages. It does not, and the difference matters most
 * to the people least likely to ask: a password can be reset from an inbox,
 * and if that were enough to open twenty years of sealed messages, then
 * whoever controls the inbox controls the archive.
 *
 * So the password gets you your account, and the Recovery Kit — the code shown
 * once at signup, which we never receive — unlocks your messages on a device
 * that has never held your key. Said here, plainly, at the moment someone is
 * forming their idea of what their password is for.
 */
export default function SetPasswordScreen() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const { adoptSession } = useAuth();

  const ticket = params.get('t') ?? '';
  const [password, setPassword] = useState('');
  const [again, setAgain] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  const bare = createApiClient({ tokens: { get: () => null, set: () => {} } });

  const tooShort = password.length > 0 && password.length < 12;
  const mismatch = again.length > 0 && again !== password;
  const ready = ticket !== '' && password.length >= 12 && again === password && !busy;

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!ready) return;
    setBusy(true); setError('');
    try {
      const tokens = await setPasswordApi(bare, ticket, password);
      adoptSession(tokens);
      navigate('/', { replace: true });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'We could not set your password.');
    } finally { setBusy(false); }
  }

  if (ticket === '') {
    return (
      <section className="auth">
        <div className="auth__card">
          <p className="auth__kicker">Legacy Architect</p>
          <h1 className="auth__title">This link is incomplete.</h1>
          <p className="auth__sub">
            Open the link from your email exactly as it was sent, or ask for a new one from the{' '}
            <Link to="/signin">sign-in page</Link>.
          </p>
        </div>
      </section>
    );
  }

  return (
    <section className="auth">
      <div className="auth__card">
        <p className="auth__kicker">Legacy Architect</p>
        <h1 className="auth__title">Choose your password.</h1>
        <p className="auth__sub">
          This is how you sign in, on any device. Length matters more than symbols — a few
          ordinary words together is both stronger and easier to live with.
        </p>
        <p className="auth__alert" role="alert">{error}</p>

        <form onSubmit={onSubmit}>
          <label className="auth__field">
            <span className="auth__label">New password</span>
            <input className="auth__input" type="password" autoComplete="new-password"
                   value={password} onChange={(e) => setPassword(e.target.value)} required />
          </label>
          <label className="auth__field">
            <span className="auth__label">Type it again</span>
            <input className="auth__input" type="password" autoComplete="new-password"
                   value={again} onChange={(e) => setAgain(e.target.value)} required />
          </label>
          <div className="la-cta-wrap">
            <button className="la-btn-primary" type="submit" disabled={!ready}>
              {busy ? 'Saving…' : 'Set password and sign in'}
            </button>
          </div>
        </form>

        {tooShort && <p className="auth__alt">A little longer — at least 12 characters.</p>}
        {mismatch && <p className="auth__alt">Those two don’t match yet.</p>}

        <p className="auth__alt">
          Your password signs you in. To open your messages on a device that has never held your
          key, you’ll also need your Recovery Kit — the code you were given when you set up. We
          never receive it, which is what stops anyone who reaches your inbox from reaching your
          messages.
        </p>
      </div>
    </section>
  );
}
